import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

const API = process.env.REACT_APP_BACKEND_URL || '';
const TOKEN_KEY = 'svb_admin_jwt';

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [firebaseAvailable] = useState(false);

  const verifyToken = useCallback(async (token) => {
    if (!token) { setLoading(false); return; }
    try {
      const res = await fetch(`${API}/api/auth/verify`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setCurrentUser({ email: data.email, uid: data.email, getIdToken: async () => token });
      } else {
        localStorage.removeItem(TOKEN_KEY);
        setCurrentUser(null);
      }
    } catch { setCurrentUser(null); }
    setLoading(false);
  }, []);

  useEffect(() => { verifyToken(localStorage.getItem(TOKEN_KEY)); }, [verifyToken]);

  const signIn = async (email, password) => {
    const res = await fetch(`${API}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      const error = new Error(err.detail || 'Login failed');
      error.code = res.status === 401 ? 'auth/invalid-credential' : 'auth/network-request-failed';
      throw error;
    }
    const data = await res.json();
    const token = data.access_token;
    localStorage.setItem(TOKEN_KEY, token);
    const user = { email, uid: email, getIdToken: async () => token };
    setCurrentUser(user);
    return user;
  };

  const logout = async () => {
    localStorage.removeItem(TOKEN_KEY);
    setCurrentUser(null);
  };

  const getToken = () => localStorage.getItem(TOKEN_KEY);

  return (
    <AuthContext.Provider value={{ currentUser, loading, firebaseAvailable, signIn, logout, getToken }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
